import { getFileUrl } from '@/clients/http'
import { format } from 'date-fns'
import { Calendar, KeyRound, Mail, Pencil, Phone, Shield, User } from 'lucide-react'

import { useUsersTable } from '@/hooks/features/users'
import { useDataTable } from '@/hooks/ui'

import { AdvancedImage } from '@/components/ui/advanced-image'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'

import { ActionDropdown, PageHeader } from '@/components/atoms'

import { UserModel as UserType } from '@/types'

import { UserDialog } from './UserDialog'

type TableColumns = 'user' | 'roles' | 'status' | 'lastActive' | 'created_at' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = ['user', 'roles', 'status', 'created_at', 'actions']

export const UsersTable = () => {
  const {
    loading,
    data,
    pagination,
    mode,
    dialogOpen,
    editUser,

    createLoading,
    updateLoading,
    setPage,
    setPageSize,
    openCreateDialog,
    openEditDialog,
    openViewDialog,
    closeDialog,

    handleCreateUser,
    handleUpdateUser,
    handleStatusToggle,
    shownPin,
    setShownPin,
    handleResetPin,
  } = useUsersTable()

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    UserType.User,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'user',
        columnLabel: () => 'User',
        cell: (data) => (
          <div className="flex items-center gap-3">
            <AdvancedImage
              avatar
              className="size-10 border border-border-subtle"
              src={
                data.image?.path
                  ? getFileUrl(data.image.path)
                  : `https://api.dicebear.com/7.x/avataaars/svg?seed=${data.email || data.name}`
              }
              alt={data.name}
              fallbackText={
                data.name
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')
                  .substring(0, 2) || 'U'
              }
              fallbackClassName="bg-fill text-xs font-medium text-text-2"
            />
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-ink">{data.name}</span>
                {data.permissions && data.permissions.length > 0 && (
                  <Badge tone="warning">
                    <Shield className="size-3" /> Overrides
                  </Badge>
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                {data.email && (
                  <span className="flex items-center gap-1 truncate text-xs text-text-2">
                    <Mail className="size-3" />
                    {data.email}
                  </span>
                )}
                {data.phone && (
                  <span className="flex items-center gap-1 truncate text-xs text-text-2">
                    <Phone className="size-3" />
                    {data.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'roles',
        columnLabel: () => 'Roles',
        cell: (data) => (
          <div className="flex flex-wrap gap-1">
            {data.roles && data.roles.length > 0 ? (
              data.roles.map((role: any) => (
                <Badge key={role._id} tone="soft">
                  {role.name}
                </Badge>
              ))
            ) : (
              <span className="text-sm text-text-2">No roles</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        columnLabel: () => 'Status',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <Badge tone={data.status === 'active' ? 'success' : 'soft'}>
              {data.status === 'active' ? 'Active' : 'Inactive'}
            </Badge>
            <Switch
              checked={data.status === 'active'}
              onCheckedChange={() => handleStatusToggle(data)}
            />
          </div>
        ),
      },
      {
        accessorKey: 'created_at',
        columnLabel: () => 'Joined',
        cell: (data) => (
          <div className="flex items-center gap-1.5 tabular-nums text-text-2">
            <Calendar className="size-4" />
            {data.created_at ? format(new Date(data.created_at), 'MMM d, yyyy') : 'N/A'}
          </div>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <ActionDropdown
              items={[
                {
                  label: 'View details',
                  permission: 'admins:details',
                  icon: User,
                  onClick: () => openViewDialog(data),
                },
                {
                  label: 'Edit',
                  permission: 'admins:update',
                  icon: Pencil,
                  onClick: () => openEditDialog(data),
                },
                {
                  label: 'Reset POS PIN',
                  permission: 'admins:update',
                  icon: KeyRound,
                  onClick: () => handleResetPin(data),
                },
              ]}
            />
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  return (
    <div>
      <PageHeader
        size="large"
        title="Users"
        onAddClick={openCreateDialog}
        addPermission="admins:create"
        addButtonText="Add staff"
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
            <div className="space-y-1">
              <CardTitle>Staff</CardTitle>
              <CardDescription className="text-text-2">
                {pagination.totalCount} {pagination.totalCount === 1 ? 'account' : 'accounts'}
                {data.length > 0 &&
                  ` · ${data.filter((u) => u.status === 'active').length} active, ${data.filter((u) => u.status !== 'active').length} inactive on this page`}
              </CardDescription>
            </div>
            <div className="flex items-center justify-between gap-2 sm:justify-end">
              <Pagination.PageSizeDropdown
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
              />
              <ColumnVisibilityDropdown
                columns={columns}
                onColumnVisibilityChange={changeColumnVisibility}
                disabledColumns={disabledColumns}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              title="No staff found"
              btnText="Add staff"
              openCreateDialog={openCreateDialog}
              createPermission="admins:create"
            />
          ) : (
            <>
              <ScrollArea className="w-full">
                <div className="inline-block min-w-full align-middle">
                  <DataTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    onRowClick={(row) => openViewDialog(row)}
                  />
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <UserDialog
        open={dialogOpen}
        mode={mode}
        editUser={editUser}
        closeDialog={closeDialog}
        onCreate={handleCreateUser}
        onUpdate={handleUpdateUser}
        loading={createLoading || updateLoading}
      />

      <Dialog open={!!shownPin} onOpenChange={(open) => !open && setShownPin(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>POS PIN for {shownPin?.name}</DialogTitle>
            <DialogDescription>
              They use it to sign in on the POS. Note it now: it is not shown again. Reset it to get a new one.
            </DialogDescription>
          </DialogHeader>
          <p className="py-4 text-center text-display tabular-nums tracking-[0.4em] text-ink">{shownPin?.pin}</p>
          <DialogFooter>
            <Button type="button" onClick={() => setShownPin(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
